import {OtsDateTime} from "../components/OtsDateTime";
import {AppAccessDeliveryAudit} from "@ots-frontend/ots-data";

export type AppAccessDeliveryAuditTableProps = {
  audits: AppAccessDeliveryAudit[];
  isLoading?: boolean;
};

const timestamp = (value: unknown): number | undefined => {
  let normalized = value;
  if (typeof normalized === "string" && /^\d+$/.test(normalized.trim())) {
    normalized = Number(normalized);
  }
  if (typeof normalized === "number" && normalized > 0 && normalized < 100_000_000_000) {
    normalized *= 1000;
  }

  const date = typeof normalized === "string" || typeof normalized === "number"
    ? new Date(normalized)
    : undefined;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : undefined;
};

const actorLabel = (audit: AppAccessDeliveryAudit): string =>
  audit.createdByPersonName ?? audit.createdByPersonId ?? audit.requestIp ?? "—";

const actorTitle = (audit: AppAccessDeliveryAudit): string | undefined => {
  const details = [
    audit.createdByPersonName ? audit.createdByPersonId : undefined,
    audit.requestIp,
    audit.userAgent,
  ].filter(Boolean);
  return details.length > 0 ? details.join(" · ") : undefined;
};

/** Compact delivery history suitable for package details and support tooling. */
export const AppAccessDeliveryAuditTable = ({audits, isLoading = false}: AppAccessDeliveryAuditTableProps) => (
  <div>
    <h3 className="font-semibold">Delivery audit</h3>
    <div className="mt-2 max-h-64 overflow-auto rounded-lg border border-[var(--ots-outline-variant)]">
      {isLoading ? (
        <p className="p-3 text-sm text-[var(--ots-text-muted)]">Loading delivery history…</p>
      ) : audits.length === 0 ? (
        <p className="p-3 text-sm text-[var(--ots-text-muted)]">No delivery activity has been recorded.</p>
      ) : (
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-[var(--ots-surface)]">
            <tr><th className="p-2">When</th><th className="p-2">Event</th><th className="p-2">Recipient</th><th className="p-2">Actor / client</th></tr>
          </thead>
          <tbody>
            {audits.map((audit) => (
              <tr key={audit.id} className="border-t border-[var(--ots-outline-variant)]">
                <td className="p-2"><OtsDateTime value={timestamp(audit.createDttm)} /></td>
                <td className="p-2">{audit.event.replace(/_/g, " ").toLowerCase()}</td>
                <td className="p-2">{audit.recipient ?? "—"}</td>
                <td className="p-2" title={actorTitle(audit)}>{actorLabel(audit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);
